import React, { useState, useEffect } from 'react';
import { API_BASE, apiFetch, showAppConfirm } from './api';
import { mergeStyles, theme } from './theme';

const styles = {
  stack: {
    display: 'grid',
    gap: '20px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  card: {
    ...theme.glassCard,
    padding: '24px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  title: {
    margin: '0 0 16px',
    color: 'var(--board-text-strong, #fff)',
    fontSize: '20px',
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  dropIndicatorRow: { height: 0 },
  dropIndicatorCell: { padding: 0, borderBottom: 'none' },
  dropIndicatorLine: { height: '2px', background: '#60a5fa', borderRadius: '999px', boxShadow: '0 0 0 1px rgba(96,165,250,0.18), 0 0 16px rgba(96,165,250,0.34)' },
  input: {
    ...theme.input,
    marginRight: '8px',
  },
  button: {
    ...theme.button,
    ...theme.primaryButton,
  },
  deleteBtn: {
    ...theme.button,
    ...theme.dangerButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  editBtn: {
    ...theme.button,
    ...theme.warningButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  saveBtn: {
    ...theme.button,
    ...theme.successButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  mutedBtn: {
    ...theme.button,
    ...theme.mutedButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  activeBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(34,197,94,0.18)',
    border: '1px solid rgba(74,222,128,0.36)',
    color: 'var(--board-success-text, #dcfce7)',
    fontSize: '12px',
    fontWeight: 800,
  },
  inactiveBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(148,163,184,0.16)',
    border: '1px solid rgba(148,163,184,0.34)',
    color: 'var(--board-text-soft, #e2e8f0)',
    fontSize: '12px',
    fontWeight: 800,
  },
  activeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--board-text-soft, rgba(255,255,255,0.82))',
    fontSize: '13px',
    fontWeight: 700,
  },
  inactiveRow: { opacity: 0.62 },
  link: {
    color: 'var(--board-text-strong, #fff)',
    textDecoration: 'none',
    border: '1px solid var(--board-border, rgba(255,255,255,0.22))',
    borderRadius: '999px',
    padding: '7px 12px',
    display: 'inline-block',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.08))',
  },
  dragCell: {
    ...theme.td,
    width: '34px',
    textAlign: 'right',
    paddingRight: '10px',
  },
  dragHandle: {
    display: 'inline-block',
    color: 'var(--board-text-soft, rgba(226,232,240,0.74))',
    cursor: 'grab',
    userSelect: 'none',
    lineHeight: 1,
    fontSize: '18px',
    fontWeight: 700,
  },
  draggingRow: {
    opacity: 0.52,
  },
};

function LoadingBays() {
  const loadingBayApiPath = `${API_BASE}/loading-bays`;
  const [loadingBays, setLoadingBays] = useState([]);
  const [name, setName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', is_active: true });
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [draggingLoadingBayId, setDraggingLoadingBayId] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const loadLoadingBays = () => {
    apiFetch(`${loadingBayApiPath}?includeInactive=true`)
      .then(res => res.json())
      .then(data => setLoadingBays(Array.isArray(data) ? data : []));
  };

  useEffect(() => { loadLoadingBays(); }, []);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addLoadingBay = async (e) => {
    e.preventDefault();
    if (!name) return;
    await apiFetch(loadingBayApiPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    setName('');
    loadLoadingBays();
  };

  const deleteLoadingBay = async (id) => {
    if (!await showAppConfirm('Ladezone dauerhaft löschen? Bestehende Zuweisungen verlieren die Ladezone-Verknüpfung.', {
      title: 'Ladezone löschen',
      confirmLabel: 'Löschen',
      danger: true,
    })) return;
    await apiFetch(`${loadingBayApiPath}/${id}`, { method: 'DELETE' });
    loadLoadingBays();
  };

  const startEdit = (loadingBay) => {
    setEditId(loadingBay.id);
    setEditForm({
      name: loadingBay.name || '',
      is_active: loadingBay.is_active !== false,
    });
  };

  const saveEdit = async (id) => {
    await apiFetch(`${loadingBayApiPath}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm)
    });
    setEditId(null);
    loadLoadingBays();
  };

  const reorderLoadingBays = async (nextLoadingBays) => {
    const response = await apiFetch(`${loadingBayApiPath}/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loading_bay_ids: nextLoadingBays.map(loadingBay => loadingBay.id) }),
    });
    const data = await response.json().catch(() => []);
    setLoadingBays(Array.isArray(data) ? data : nextLoadingBays);
  };

  const moveLoadingBay = (list, fromIndex, toIndex) => {
    if (fromIndex === -1 || toIndex < 0) return list;
    const next = [...list];
    const [item] = next.splice(fromIndex, 1);
    if (!item) return list;
    next.splice(toIndex, 0, item);
    return next;
  };

  const handleDragStart = (loadingBayId, event) => {
    if (editId !== null) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(loadingBayId));
    setDraggingLoadingBayId(loadingBayId);
    setDropIndex(loadingBays.findIndex(loadingBay => loadingBay.id === loadingBayId));
  };

  const handleRowDragOver = (index, event) => {
    if (draggingLoadingBayId === null) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const nextIndex = event.clientY < rect.top + rect.height / 2 ? index : index + 1;
    if (nextIndex !== dropIndex) setDropIndex(nextIndex);
  };

  const handleTableDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDropIndex(null);
    }
  };

  const handleDrop = async (event) => {
    if (draggingLoadingBayId === null || dropIndex === null) return;
    event.preventDefault();
    const fromIndex = loadingBays.findIndex(loadingBay => loadingBay.id === draggingLoadingBayId);
    if (fromIndex === -1) {
      setDraggingLoadingBayId(null);
      setDropIndex(null);
      return;
    }

    const targetIndex = dropIndex > fromIndex ? dropIndex - 1 : dropIndex;
    if (targetIndex === fromIndex) {
      setDraggingLoadingBayId(null);
      setDropIndex(null);
      return;
    }

    const nextLoadingBays = moveLoadingBay(loadingBays, fromIndex, targetIndex);
    setDraggingLoadingBayId(null);
    setDropIndex(null);
    setLoadingBays(nextLoadingBays);
    try {
      await reorderLoadingBays(nextLoadingBays);
    } catch {
      loadLoadingBays();
    }
  };

  const handleDragEnd = () => {
    setDraggingLoadingBayId(null);
    setDropIndex(null);
  };

  const isMobile = viewportWidth < 760;
  const columnCount = 6;

  return (
    <div style={styles.stack}>
      <div style={styles.card}>
        <h2 style={styles.title}>Neue Ladezone anlegen</h2>
        <form style={{ ...styles.form, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }} onSubmit={addLoadingBay}>
          <input style={{ ...styles.input, width: '100%', marginRight: 0 }} placeholder="Name (z.B. Ladezone 1)" value={name} onChange={e => setName(e.target.value)} />
          <button style={{ ...styles.button, width: isMobile ? '100%' : 'auto' }} type="submit">Hinzufügen</button>
        </form>
      </div>

      <div style={styles.card}>
        <h2 style={styles.title}>Ladezonen ({loadingBays.length})</h2>
        {isMobile && (
          <p style={{ marginTop: 0, marginBottom: '16px', color: 'var(--board-text-muted, rgba(226,232,240,0.68))', fontSize: '13px', lineHeight: 1.5 }}>
            Die Reihenfolge der Ladezonen kann aktuell nur in der Desktop-Ansicht per Drag geändert werden.
          </p>
        )}
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Dashboard</th>
                <th style={styles.th}>Aktion</th>
                <th style={{ ...styles.th, width: '34px', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody onDrop={handleDrop} onDragOver={event => draggingLoadingBayId !== null && event.preventDefault()} onDragLeave={handleTableDragLeave}>
              {loadingBays.map((loadingBay, index) => (
                <React.Fragment key={loadingBay.id}>
                  {dropIndex === index && draggingLoadingBayId !== null && (
                    <tr style={styles.dropIndicatorRow}>
                      <td colSpan={columnCount} style={styles.dropIndicatorCell}>
                        <div style={styles.dropIndicatorLine}></div>
                      </td>
                    </tr>
                  )}
                  <tr
                    style={{
                      ...(loadingBay.is_active === false ? styles.inactiveRow : {}),
                      ...(draggingLoadingBayId === loadingBay.id ? styles.draggingRow : {}),
                    }}
                    onDragOver={event => handleRowDragOver(index, event)}
                  >
                  <td style={styles.td}>{loadingBay.id}</td>
                  <td style={styles.td}>
                    {editId === loadingBay.id
                      ? <input style={mergeStyles(styles.input, { width: '100%', maxWidth: '260px' })} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                      : <strong>{loadingBay.name}</strong>}
                  </td>
                  <td style={styles.td}>
                    {editId === loadingBay.id ? (
                      <label style={styles.activeToggle}>
                        <input
                          type="checkbox"
                          checked={editForm.is_active}
                          onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })}
                        />
                        Aktiv
                      </label>
                    ) : (
                      <span style={loadingBay.is_active === false ? styles.inactiveBadge : styles.activeBadge}>
                        {loadingBay.is_active === false ? 'Inaktiv' : 'Aktiv'}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {loadingBay.is_active === false ? '-' : (
                      <a style={styles.link} href={`/display/loadingbay/${loadingBay.id}`} target="_blank" rel="noreferrer">
                        Dashboard öffnen
                      </a>
                    )}
                  </td>
                  <td style={styles.td}>
                    {editId === loadingBay.id ? (
                      <>
                        <button style={styles.saveBtn} onClick={() => saveEdit(loadingBay.id)}>Speichern</button>
                        <button style={styles.mutedBtn} onClick={() => setEditId(null)}>Abbrechen</button>
                      </>
                    ) : (
                      <>
                        <button style={styles.editBtn} onClick={() => startEdit(loadingBay)}>Bearbeiten</button>
                        <button style={styles.deleteBtn} onClick={() => deleteLoadingBay(loadingBay.id)}>Löschen</button>
                      </>
                    )}
                  </td>
                  <td style={styles.dragCell}>
                    {!isMobile && editId !== loadingBay.id && (
                      <span
                        style={{ ...styles.dragHandle, cursor: draggingLoadingBayId === loadingBay.id ? 'grabbing' : 'grab' }}
                        draggable
                        onDragStart={event => handleDragStart(loadingBay.id, event)}
                        onDragEnd={handleDragEnd}
                        title="Ladezone verschieben"
                        aria-label="Ladezone verschieben"
                      >
                        ☰
                      </span>
                    )}
                  </td>
                </tr>
                </React.Fragment>
              ))}
              {dropIndex === loadingBays.length && draggingLoadingBayId !== null && (
                <tr style={styles.dropIndicatorRow}>
                  <td colSpan={columnCount} style={styles.dropIndicatorCell}>
                    <div style={styles.dropIndicatorLine}></div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default LoadingBays;

